/* eslint-disable @typescript-eslint/explicit-function-return-type */
import React from "react";
import { Button } from "./ui/button";
import { X, Download, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { type ExportProgress } from "../types";
import { CustomProgress } from "./CustomProgress";

interface ExportProgressModalProps {
  progress: ExportProgress;
  onCancel: () => void;
  onClose: () => void;
  isOpen: boolean;
}

export function ExportProgressModal({
  progress,
  onCancel,
  onClose,
  isOpen,
}: ExportProgressModalProps) {
  if (!isOpen) return null;

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const getProgressPercentage = () => {
    if (progress.totalRecords === 0) return 0;
    return Math.round(
      (progress.processedRecords / progress.totalRecords) * 100
    );
  };

  const getStageMessage = () => {
    switch (progress.stage) {
      case "preparing":
        return "Preparing export...";
      case "fetching":
        return `Fetching data (${progress.completedBatches}/${progress.totalBatches} batches)`;
      case "processing":
        return "Processing data for export...";
      case "generating":
        return "Generating CSV file...";
      case "complete":
        return "Export completed successfully!";
      case "error":
        return `Export failed: ${progress.error}`;
      default:
        return "Exporting...";
    }
  };

  const getStageIcon = () => {
    switch (progress.stage) {
      case "complete":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "error":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Download className="h-5 w-5 text-blue-500 animate-pulse" />;
    }
  };

  const showCancelButton =
    progress.isExporting &&
    progress.stage !== "complete" &&
    progress.stage !== "error";
  const showCloseButton =
    progress.stage === "complete" || progress.stage === "error";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-lg mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            {getStageIcon()}
            <h3 className="text-lg font-semibold">Exporting Data</h3>
          </div>

          {showCloseButton && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-2">{getStageMessage()}</p>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-700 font-medium">
              <span>Progress</span>
              <span>{getProgressPercentage()}%</span>
            </div>
            {/* Enhanced Progress Bar with better visibility */}
            <div className="relative">
              <CustomProgress value={getProgressPercentage()} />
              {/* Add a subtle animation for active progress */}
              {progress.isExporting && progress.stage === "fetching" && (
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse rounded-full"></div>
              )}
            </div>
          </div>

          {/* Detailed Progress Info */}
          {progress.isExporting && progress.stage === "fetching" && (
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Records:</span>
                  <span className="font-mono text-gray-900">
                    {progress.processedRecords.toLocaleString()} /{" "}
                    {progress.totalRecords.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Batches:</span>
                  <span className="font-mono text-gray-900">
                    {progress.completedBatches} / {progress.totalBatches}
                  </span>
                </div>
              </div>

              {progress.estimatedTimeRemaining && (
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="text-gray-500 flex items-center">
                    <Clock className="h-3 w-3 mr-1" />
                    Est. time remaining:
                  </span>
                  <span className="font-mono text-blue-600">
                    {formatTime(progress.estimatedTimeRemaining)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Completion Summary */}
          {progress.stage === "complete" && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium text-green-800">
                  Export Successful
                </span>
              </div>

              <div className="space-y-1 text-sm text-green-700">
                <div className="flex justify-between">
                  <span>Total records exported:</span>
                  <span className="font-mono">
                    {progress.processedRecords.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Batches processed:</span>
                  <span className="font-mono">{progress.completedBatches}</span>
                </div>
                {progress.totalExportTime && (
                  <div className="flex justify-between font-medium">
                    <span>Total time taken:</span>
                    <span className="font-mono text-green-800">
                      {formatTime(progress.totalExportTime)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Error Summary */}
          {progress.stage === "error" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium text-red-800">
                  Export Failed
                </span>
              </div>

              <p className="text-sm text-red-700">{progress.error}</p>

              {progress.processedRecords > 0 && (
                <div className="mt-2 pt-2 border-t border-red-200">
                  <div className="flex justify-between text-sm text-red-600">
                    <span>Records processed before failure:</span>
                    <span className="font-mono">
                      {progress.processedRecords.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-2">
            {showCancelButton && (
              <Button
                variant="outline"
                onClick={onCancel}
                className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
              >
                Cancel Export
              </Button>
            )}

            {showCloseButton && <Button onClick={onClose}>Close</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
